<article class="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
  <!-- Header -->
  <div class="flex w-full items-center gap-4 px-5 py-4 transition-colors hover:bg-muted/30">
    <!-- Toggle region (left): badges, contract id, start/end, trial state -->
    <button
      type="button"
      (click)="toggleExpand()"
      class="flex min-w-0 flex-1 items-center gap-4 text-left"
      [attr.aria-expanded]="expanded()"
    >
      <hlm-badge [variant]="contract().mainContract ? 'default' : 'secondary'">
        {{ contract().mainContract ? 'Main' : 'Sub' }}
      </hlm-badge>

      <!-- EB-3858: related user account(s) deactivated -->
      @if (relatedInactive()) {
        <hlm-badge variant="destructive">inactive</hlm-badge>
      }

      <span class="font-display text-lg font-medium text-foreground whitespace-nowrap">
        Contract <span class="font-mono-feat">{{ contract().id }}</span>
      </span>

      <span class="flex-1 text-sm text-muted-foreground whitespace-nowrap">
        start {{ contract().startDate | date:'dd.MM.yyyy' }}
        @if (contract().endDate) {
          <span class="ml-3 text-muted-foreground">· end {{ contract().endDate | date:'dd.MM.yyyy' }}</span>
        }
      </span>

      @if (trialInfo().isFreeTrial) {
        @if (trialInfo().trialState === 'expired') {
          <hlm-badge variant="destructive">Expired free trial</hlm-badge>
        } @else {
          <hlm-badge variant="secondary">Free trial</hlm-badge>
        }
      } @else if (contract().deactivated) {
        <hlm-badge variant="destructive">Deactivated</hlm-badge>
      }
    </button>

    <!-- EB-3859: related UA/PS summary (name, ids + copy). Single-account only;
         multi-account contracts show a compact hint instead. -->
    @if (accountsLoading()) {
      <hlm-spinner size="14" class="flex-none text-muted-foreground/40" />
    } @else if (isMultiAccount()) {
      <span class="flex-none text-sm italic text-muted-foreground">Multi Account contract</span>
    } @else if (singleAccount(); as acc) {
      <div class="grid flex-none grid-cols-[auto_auto] items-center gap-x-3 gap-y-0.5 text-sm">
        <!-- Row A: user name · UA id -->
        <span class="truncate text-foreground">{{ acc.userName }}</span>
        <span class="flex items-center gap-1 text-muted-foreground">
          <span class="font-mono-feat">UA {{ acc.userAccountId }}</span>
          <button
            type="button"
            (click)="copyId($event, 'ua', acc.userAccountId)"
            [attr.aria-label]="'Copy user account id'"
            class="grid h-4 w-4 place-items-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <hlm-icon [name]="copiedField() === 'ua' ? 'lucideCheck' : 'lucideCopy'" size="0.7rem" />
          </button>
        </span>
        <!-- Row B: phone number(s) · PS id -->
        <span class="flex items-center gap-1.5 text-foreground">
          <hlm-icon name="lucidePhone" size="0.7rem" class="text-muted-foreground" />
          @if (acc.numbers.length > 1) {
            <span [title]="numbersTitle(acc)" class="cursor-help underline decoration-dotted underline-offset-2">{{ acc.numbers.length }} numbers</span>
          } @else if (acc.numbers.length === 1) {
            <span class="font-mono-feat">{{ acc.numbers[0].value }}</span>
          } @else {
            <span class="italic text-muted-foreground/60">no number</span>
          }
        </span>
        <span class="flex items-center gap-1 text-muted-foreground">
          <span class="font-mono-feat">PS {{ acc.phoneServiceId }}</span>
          <button
            type="button"
            (click)="copyId($event, 'ps', acc.phoneServiceId)"
            [attr.aria-label]="'Copy phone service id'"
            class="grid h-4 w-4 place-items-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <hlm-icon [name]="copiedField() === 'ps' ? 'lucideCheck' : 'lucideCopy'" size="0.7rem" />
          </button>
        </span>
      </div>
    } @else {
      <span class="flex-none text-sm italic text-muted-foreground/70">no account</span>
    }

    <!-- Toggle region (right): chevron -->
    <button
      type="button"
      (click)="toggleExpand()"
      class="flex-none"
      [attr.aria-label]="expanded() ? 'Collapse contract' : 'Expand contract'"
    >
      <hlm-icon
        [name]="expanded() ? 'lucideChevronUp' : 'lucideChevronDown'"
        size="1rem"
        class="text-muted-foreground"
      />
    </button>
  </div>

  <!-- Body -->
  @if (expanded()) {
    <div class="border-t border-border">
      @if (loadingRevisions()) {
        <div class="flex items-center gap-3 p-5 text-sm text-muted-foreground">
          <hlm-spinner size="18" /> Loading revisions…
        </div>
      } @else {
        <!-- Controls row -->
        <div class="flex flex-wrap items-center gap-3 border-b border-border px-5 py-3">
          <hlm-checkbox
            [checked]="showPastChanges()"
            (checkedChange)="showPastChanges.set($event)"
            label="Show past changes"
          />

          <button
            type="button"
            (click)="toggleSortOrder()"
            class="text-base text-muted-foreground transition-colors hover:text-foreground"
          >
            {{ sortNewest() ? 'Sorted: Newest on top' : 'Sorted: Oldest on top' }}
          </button>

          <span class="flex-1"></span>

          <!-- Accounts & Numbers menu -->
          <hlm-menu align="end" width="min-w-[420px]">
            <button hlmMenuTrigger hlmBtn variant="outline" size="sm" (click)="loadQuickAccounts()">
              <hlm-icon name="lucideUsers" size="0.9rem" />
              Accounts &amp; Numbers
              <hlm-icon name="lucideChevronDown" size="0.8rem" />
            </button>
            <ng-template hlmMenuContent>
              <div class="max-h-[70vh] overflow-y-auto p-2">
                @if (accountsLoading()) {
                  <div class="flex items-center gap-2 px-3 py-3 text-sm text-muted-foreground">
                    <hlm-spinner size="14" /> Loading…
                  </div>
                } @else if (quickAccounts().length === 0) {
                  <div class="px-3 py-3 text-sm text-muted-foreground">No accounts found</div>
                } @else {
                  @for (acc of quickAccounts(); track acc.userAccountId) {
                    <div class="mb-1 rounded-md bg-muted/30 p-2.5">
                      <div class="mb-1 flex items-center gap-2 text-sm text-foreground">
                        <hlm-icon name="lucideUserRound" size="0.9rem" class="text-primary" />
                        <strong class="font-mono-feat">{{ acc.userAccountId }}</strong>
                        <span>— {{ acc.userName }}</span>
                      </div>
                      @if (acc.numbers.length > 0) {
                        @for (num of acc.numbers; track num.value) {
                          <div class="flex items-center gap-2 pl-6 text-sm text-muted-foreground">
                            <hlm-icon name="lucidePhone" size="0.75rem" />
                            <span class="font-mono-feat">{{ num.value }}</span>
                            <span class="font-mono-feat text-sm uppercase tracking-wider text-muted-foreground/70">{{ num.type }}</span>
                          </div>
                        }
                      } @else {
                        <div class="pl-6 text-sm italic text-muted-foreground/60">No numbers</div>
                      }
                    </div>
                  }
                }
              </div>
            </ng-template>
          </hlm-menu>
        </div>

        <!-- Two-column: revisions + actions -->
        <div class="grid gap-5 p-5 lg:grid-cols-[minmax(0,1fr)_280px]">
          <!-- Left: Revisions -->
          <div>
            @if (filteredRevisions().length > 0) {
              <cmt-revision-list
                [revisions]="filteredRevisions()"
                [currency]="currency()"
                [activeIndex]="filteredActiveIndex()"
                [featureNames]="featureNames()"
                [extensionNames]="extensionNames()"
              />
            } @else if (revisionsLoadFailed()) {
              <!-- "We do not know the revisions" is not "there are none" — the same
                   distinction the Move Start Date gate below is built on. Saying
                   "No revisions to display." here was the conflation, in the one place the
                   operator actually reads (PR #42 re-review defect 1). -->
              <div class="flex flex-col items-center gap-3 rounded-xl border border-dashed border-destructive/40 p-6 text-center text-sm">
                <span class="text-muted-foreground">
                  The revisions for this contract could not be loaded, so what it contains is unknown.
                </span>
                <button hlmBtn variant="outline" size="sm" (click)="retryRevisionLoad()">
                  <hlm-icon name="lucideRefreshCw" size="0.9rem" /> Retry
                </button>
              </div>
            } @else {
              <div class="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
                No revisions to display.
              </div>
            }
          </div>

          <!-- Right: Actions -->
          <div class="flex flex-col gap-1.5">
            <p class="font-mono-feat mb-1 text-sm uppercase tracking-[0.2em] text-muted-foreground">
              Actions
            </p>
            <!-- EB-3922: Extend Free Trial — available for any free trial (incl. a
                 deactivated/expired one, whose actions column is otherwise empty),
                 disabled once the 3× cap is reached. Shown before the branch split so
                 it is not duplicated across the deactivated/active cases. -->
            @if (trialInfo().isFreeTrial) {
              <button
                hlmBtn
                variant="default"
                size="sm"
                [disabled]="trialCapReached()"
                (click)="openExtendFreeTrial()"
              >
                <hlm-icon name="lucideCalendarPlus" size="0.9rem" /> Extend Free Trial
              </button>
              @if (trialCapReached()) {
                <span class="text-sm italic text-muted-foreground/70">
                  Extended {{ maxTrialExtensions }}× already — limit reached
                </span>
              }
            }
            @if (contract().deactivated) {
              @if (!session.customer()?.endDate) {
                <button hlmBtn variant="default" size="sm" (click)="reactivateContract()">
                  <hlm-icon name="lucideRotateCcw" size="0.9rem" />
                  Reactivate
                </button>
              }
            } @else {
              <button hlmBtn variant="outline" size="sm" (click)="openAddExtension()">
                <hlm-icon name="lucidePlus" size="0.9rem" /> Add Extension
              </button>
              <button hlmBtn variant="outline" size="sm" (click)="openRemoveExtension()">
                <hlm-icon name="lucideMinus" size="0.9rem" /> Remove Extension
              </button>
              @if (contract().mainContract && contract().editable) {
                <button hlmBtn variant="outline" size="sm" (click)="openChangePlan()">
                  <hlm-icon name="lucideRefreshCw" size="0.9rem" /> Change Plan
                </button>
                <!-- EB-4005: one-click downgrade to Basic (avoids cancel-first detour) -->
                <button hlmBtn variant="destructive" size="sm" (click)="openChangeToBasic()">
                  <hlm-icon name="lucideArrowDown" size="0.9rem" /> Change to Basic
                </button>
              }
              <button hlmBtn variant="outline" size="sm" (click)="openChangeDiscountPlan()">
                <hlm-icon name="lucidePercent" size="0.9rem" /> Discount (Plan)
              </button>
              <button hlmBtn variant="outline" size="sm" (click)="openChangeDiscountExtension()">
                <hlm-icon name="lucidePercent" size="0.9rem" /> Discount (Extension)
              </button>
              @if (contract().terminable) {
                <button hlmBtn variant="destructive" size="sm" (click)="openEndContract()">
                  <hlm-icon name="lucideCircleX" size="0.9rem" /> End Contract
                </button>
              }
              <!-- Also gated on revisionsLoaded(): the bounds are derived from the revisions,
                   and on a failed load `revisions()` is empty, which makes the range come out
                   as min > max — a form that can never be satisfied (PR #42 review, roundup
                   #3). "No revision covers today" and "we do not know the revisions" are
                   different states and only the first one has a sensible fallback. -->
              @if (contract().mainContract && contract().canChangeStartDate && revisionsLoaded()) {
                <button hlmBtn variant="outline" size="sm" (click)="openMoveStartDate()">
                  <hlm-icon name="lucideCalendar" size="0.9rem" /> Move Start Date
                </button>
              }
              <button
                hlmBtn
                variant="outline"
                size="sm"
                [disabled]="!upgradeAvailable()"
                (click)="openUpgradeExtension()"
              >
                <hlm-icon name="lucideArrowUp" size="0.9rem" /> Upgrade Extension
              </button>
              @if (!upgradeAvailable()) {
                <span class="text-sm italic text-muted-foreground/70">No upgrade option available</span>
              }
            }
          </div>
        </div>
      }
    </div>
  }
</article>
